import React from 'react';
import {BrowserRouter, Routes, Route, Navigate} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {queryClient, trpc, trpcClient} from '@common-web/lib/trpc';
import {Toaster} from '@common-web/components/ui/toaster';
import {ScrollRestoration} from '@common-web/components/scroll-restoration';
import {Layout} from '@internal/layouts/layout';
import {LoginPage} from '@common-web/pages/loginPage';
import {ForgotPasswordPage} from '@common-web/pages/forgotPasswordPage';
import {ResetPasswordPage} from '@common-web/pages/resetPasswordPage';
import {SettingsPage} from '@common-web/pages/settingsPage';
import {AuthGuard} from '@common-web/components/auth-guard';
import {RequirePermission} from '@common-web/components/require-permission';
import {CODE_PERMISSIONS} from '@common/permissions';
import {EnrollmentSitesList} from '@internal/pages/enrollment/EnrollmentSitesList';
import {EnrollmentSiteBuilder} from '@internal/pages/enrollment/EnrollmentSiteBuilder';
import {EnrollmentSitePreview} from '@internal/pages/enrollment/EnrollmentSitePreview';
import {EnrollmentLocationsPage} from '@internal/pages/enrollment/EnrollmentLocationsPage';
import {EnrollmentActivityPage} from '@internal/pages/enrollment/EnrollmentActivityPage';
import {GroupPipelinePage} from '@internal/pages/group-pipeline/GroupPipelinePage';
import {GroupPipelineDetailPage} from '@internal/pages/group-pipeline/GroupPipelineDetailPage';
import {PipelineBuilderPage} from '@internal/pages/group-pipeline/PipelineBuilderPage';
import {InternalUsers, InternalCreateUser, InternalUserDetail} from '@internal/pages/users';
// Unified Accounts page — additive alongside the legacy Deals/RFC pages, which
// stay fully wired. Surfaced in the sidebar behind the `internal.accounts` flag.
import {AccountsList} from '@client/pages/account/AccountsList';
import {AccountDetail} from '@client/pages/account/AccountDetail';
import {ClientCreateRfc} from '@client/pages/rfc/ClientCreateRfc';
import {ClientRfcDashboard} from '@client/pages/rfc/ClientDashboard';
import {ClientRfcReview} from '@client/pages/rfc/ClientRfcReview';
import {InternalRfcSubmissions} from '@internal/pages/rfc/InternalRfcSubmissions';
import {CbsQueuePage} from '@internal/pages/rfc/CbsQueuePage';
import {OnboardingPipelineDashboard} from '@internal/pages/onboarding/OnboardingPipelineDashboard';
import {OnboardingGroupDetail} from '@internal/pages/onboarding/OnboardingGroupDetail';
import {OnboardingMaintenanceView} from '@internal/pages/onboarding/OnboardingMaintenanceView';
import {OnboardingTemplateList} from '@internal/pages/onboarding/OnboardingTemplateList';
import {OnboardingTemplateDetail} from '@internal/pages/onboarding/OnboardingTemplateDetail';
import {
  EligibilityImport,
  GroupDetails,
  GroupMembers,
  GroupList,
  GroupMemberDetails,
  MemberAdd,
  MemberSearch,
  CreateVipMember,
} from '@client/pages';
import {SignupPage} from '@common-web/pages/signupPage';
import {PortalProvider} from '@common-web/providers/portalProvider';
import {AnalyticsProvider} from '@common-web/providers/analyticsProvider';
import {AnalyticsIdentify} from '@common-web/components/analytics-identify';
import {TenantThemeProvider} from '@common-web/providers/tenantThemeProvider';
import type {TenantThemeConfig} from '@common-web/types/tenantTheme';
import {JobRunsList} from '@internal/pages/jobs/JobRunsList';
import {JobRunDetail} from '@internal/pages/jobs/JobRunDetail';
import {VbaSyncRunsList} from '@internal/pages/vba-sync/VbaSyncRunsList';
import {VbaSyncRunDetail} from '@internal/pages/vba-sync/VbaSyncRunDetail';
import {VbaSyncCasRunsList} from '@internal/pages/vba-sync-cas/VbaSyncCasRunsList';
import {VbaSyncCasRunDetail} from '@internal/pages/vba-sync-cas/VbaSyncCasRunDetail';
import {VbaSyncCasMappings} from '@internal/pages/vba-sync-cas/VbaSyncCasMappings';
import {InvoiceList} from '@internal/pages/invoices/list';
import {InvoiceDetail} from '@internal/pages/invoices/detail';
import {GenerationBatchDetail} from '@internal/pages/invoices/generation-batch-detail';
import {ClaimsTrackingDashboard} from '@internal/pages/claims/ClaimsTrackingDashboard';
import {ActivityLog} from '@internal/pages/audit/ActivityLog';
import {ClientsList} from '@internal/pages/clients/list';
import {RolesAndPermissions} from '@internal/pages/permissions/RolesAndPermissions';
import {PermissionDetail} from '@internal/pages/permissions/PermissionDetail';
import {RoleDetail} from '@internal/pages/roles/RoleDetail';
import {RoleNew} from '@internal/pages/roles/RoleNew';
import {InternalFeatures} from '@internal/pages/internal-features/list';
import {GroupBuildWizard} from '@internal/pages/groups/GroupBuildWizard';
import {InternalTickets} from '@internal/pages/tickets/InternalTickets';
import {InternalTicketDetail} from '@internal/pages/tickets/InternalTicketDetail';
// CBS / Concierge internal-portal theme. Mirrors the warm, flat aesthetic of
// ~/cbs-portal: a sand content surface, flat (non-navy) page headers, and the
// CBS logo in the sidebar. Color tokens themselves live in `index.css`.
const cbsTheme: TenantThemeConfig = {
  logo: '/logos/cbs-logo.svg',
  header: {variant: 'flat'},
  layout: {contentClassName: 'bg-background'},
};

export function App() {
  // The preview mounts its own router, which can't nest inside the app's BrowserRouter —
  // so the preview path renders a standalone tree, everything else the normal app.
  const previewSlug = window.location.pathname.match(
    /^\/enrollment-sites\/([^/]+)\/preview\/?$/,
  )?.[1];

  return (
    <AnalyticsProvider app='internal'>
      <AnalyticsIdentify />
      <PortalProvider portalType='internal'>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <TenantThemeProvider theme={cbsTheme}>
            {previewSlug ? (
              <EnrollmentSitePreview slug={previewSlug} />
            ) : (
            <BrowserRouter>
              <ScrollRestoration />
              <Routes>
                <Route
                  element={
                    <AuthGuard>
                      <Layout />
                    </AuthGuard>
                  }
                >
                  {/* Internal users' home. */}
                  <Route index element={<Navigate to='/groups' replace />} />
                  <Route path='groups' element={<GroupList />} />
                  <Route path='groups/new' element={<GroupBuildWizard />} />
                  {/* Backed by vba.members.search, which requires eligibility.view —
                    same gate as the sidebar entry. */}
                  <Route
                    path='member-search'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.eligibility.view.key}
                        redirectTo='/groups'
                      >
                        <MemberSearch />
                      </RequirePermission>
                    }
                  />
                  <Route path='groups/:groupId' element={<GroupDetails />} />

                  <Route path='groups/:groupId/members' element={<GroupMembers />} />
                  <Route path='groups/:groupId/members/add' element={<MemberAdd />} />
                  <Route path='groups/:groupId/members/import' element={<EligibilityImport />} />
                  <Route path='members/add' element={<MemberAdd />} />
                  <Route path='members/import' element={<EligibilityImport />} />
                  <Route path='members/add-vip' element={<CreateVipMember />} />
                  <Route path='member/:id' element={<GroupMemberDetails />} />

                  {/* Enrollment site builder (enrollment.view to see, enrollment.manage to edit). */}
                  <Route
                    element={
                      <div className='mx-auto w-full max-w-7xl px-6 py-6'>
                        <RequirePermission
                          permission={CODE_PERMISSIONS.enrollment.view.key}
                          redirectTo='/groups'
                        />
                      </div>
                    }
                  >
                    <Route path='enrollment-sites' element={<EnrollmentSitesList />} />
                    <Route path='enrollment-sites/activity' element={<EnrollmentActivityPage />} />
                    <Route path='enrollment-sites/:groupSlug' element={<EnrollmentSiteBuilder />} />
                    <Route
                      path='enrollment-sites/:groupSlug/locations'
                      element={<EnrollmentLocationsPage />}
                    />
                  </Route>

                  {/* Groups section (groupPipeline.view to see). */}
                  <Route
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.groupPipeline.view.key}
                        redirectTo='/groups'
                      />
                    }
                  >
                    <Route path='group-pipeline' element={<GroupPipelinePage />} />
                    <Route
                      path='group-pipeline/:vbaGroupId'
                      element={<GroupPipelineDetailPage />}
                    />
                    <Route path='group-pipeline/builder' element={<PipelineBuilderPage />} />
                  </Route>

                  <Route path='invoices' element={<InvoiceList />} />
                  <Route path='invoices/generation/:batchId' element={<GenerationBatchDetail />} />
                  <Route path='invoices/:invoiceKey' element={<InvoiceDetail />} />

                  <Route
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.tenant.accessAll.key}
                        redirectTo='/groups'
                      />
                    }
                  >
                    {/* User Management Pages */}
                    <Route path='users' element={<InternalUsers />} />
                    <Route path='users/new' element={<InternalCreateUser />} />
                    <Route path='users/:id' element={<InternalUserDetail />} />
                  </Route>
                  {/* Clients (tenant feature flags) */}
                  <Route
                    path='clients'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.clients.manageFeatures.key}
                        redirectTo='/groups'
                      >
                        <ClientsList />
                      </RequirePermission>
                    }
                  />
                  {/* Development requests — same gate as the sidebar entry. */}
                  <Route
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.tickets.manageInternal.key}
                        redirectTo='/groups'
                      />
                    }
                  >
                    <Route path='tickets' element={<InternalTickets />} />
                    <Route path='tickets/:id' element={<InternalTicketDetail />} />
                  </Route>
                  {/* Internal portal (org-wide) feature flags */}
                  <Route
                    path='internal-features'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.internalConfig.manage.key}
                        redirectTo='/groups'
                      >
                        <InternalFeatures />
                      </RequirePermission>
                    }
                  />
                  {/* Deals Pages */}
                  <Route path='rfc/create' element={<ClientCreateRfc />} />
                  <Route path='rfc/admin' element={<ClientRfcDashboard />} />
                  <Route path='rfc/admin/:rfcId' element={<ClientRfcReview />} />
                  {/* Backed by a tenant.access_all-gated endpoint, same gate as
                    the sidebar entry. */}
                  <Route
                    path='rfc/submissions'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.tenant.accessAll.key}
                        redirectTo='/groups'
                      >
                        <InternalRfcSubmissions />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path='rfc/cbs-queue'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.rfc.cbsQueue.key}
                        redirectTo='/groups'
                      >
                        <CbsQueuePage />
                      </RequirePermission>
                    }
                  />

                  {/* Accounts (unified pipeline page) — additive. Also gated in
                    the sidebar by the `internal.accounts` menu flag; the
                    permission guard matches the sidebar's tenant.access_all
                    gate (the backing endpoints admit internal users only when
                    they hold it). */}
                  <Route
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.tenant.accessAll.key}
                        redirectTo='/groups'
                      />
                    }
                  >
                    <Route path='accounts' element={<AccountsList />} />
                    <Route path='accounts/new' element={<AccountsList />} />
                    <Route path='accounts/:accountsId' element={<AccountDetail />} />
                  </Route>

                  {/* Onboarding Pipeline */}
                  <Route path='onboarding' element={<OnboardingPipelineDashboard />} />
                  <Route path='onboarding/templates' element={<OnboardingTemplateList />} />
                  <Route
                    path='onboarding/templates/:templateId'
                    element={<OnboardingTemplateDetail />}
                  />
                  <Route path='onboarding/maintenance' element={<OnboardingMaintenanceView />} />
                  <Route path='onboarding/:onboardingId' element={<OnboardingGroupDetail />} />

                  <Route path='jobs' element={<JobRunsList />} />
                  <Route path='jobs/:id' element={<JobRunDetail />} />

                  {/* Activity log — pathless guard on the audit.view permission. */}
                  <Route
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.audit.view.key}
                        redirectTo='/groups'
                      />
                    }
                  >
                    {/* Activity / Change Log */}
                    <Route path='audit/activity' element={<ActivityLog />} />
                  </Route>

                  {/* Claims tracking (internal admin) */}
                  <Route
                    path='claims'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.claimsTracking.view.key}
                        redirectTo='/groups'
                      >
                        <ClaimsTrackingDashboard />
                      </RequirePermission>
                    }
                  />

                  {/* VBA Sync Runs */}
                  <Route path='vba-sync' element={<VbaSyncRunsList />} />
                  <Route path='vba-sync/:id' element={<VbaSyncRunDetail />} />
                  {/* VBA Sync Runs V2 (S3-sourced) */}
                  <Route path='vba-sync-cas' element={<VbaSyncCasRunsList />} />
                  {/* Before the :id route — a run id is a timestamp, so `mappings` must not match it. */}
                  <Route path='vba-sync-cas/mappings' element={<VbaSyncCasMappings />} />
                  <Route path='vba-sync-cas/:id' element={<VbaSyncCasRunDetail />} />
                  {/* Roles & Permissions — same any-of gate as the sidebar entry */}
                  <Route
                    path='permissions'
                    element={
                      <RequirePermission
                        permission={[
                          CODE_PERMISSIONS.roles.manage.key,
                          CODE_PERMISSIONS.permissions.manage.key,
                        ]}
                        redirectTo='/groups'
                      >
                        <RolesAndPermissions />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path='permissions/:id'
                    element={
                      // Unlike the tabbed /permissions page (whose tabs degrade
                      // per-key), every permissionsRouter procedure behind this
                      // detail page requires `permissions.manage` — a
                      // roles.manage-only user would only see 403 content.
                      <RequirePermission
                        permission={CODE_PERMISSIONS.permissions.manage.key}
                        redirectTo='/groups'
                      >
                        <PermissionDetail />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path='roles/new'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.roles.manage.key}
                        redirectTo='/groups'
                      >
                        <RoleNew />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path='roles/:id'
                    element={
                      <RequirePermission
                        permission={CODE_PERMISSIONS.roles.manage.key}
                        redirectTo='/groups'
                      >
                        <RoleDetail />
                      </RequirePermission>
                    }
                  />

                  {/* Settings */}
                  <Route path='settings' element={<SettingsPage />} />
                  {/* Default redirect */}
                  <Route path='*' element={<Navigate to='/groups' replace />} />
                </Route>
                <Route path='/signup' element={<SignupPage />} />
                <Route path='/login' element={<LoginPage />} />
                <Route path='/forgot-password' element={<ForgotPasswordPage />} />
                <Route path='/reset-password' element={<ResetPasswordPage />} />
              </Routes>
            </BrowserRouter>
            )}
            <Toaster />
          </TenantThemeProvider>
        </QueryClientProvider>
      </trpc.Provider>
      </PortalProvider>
    </AnalyticsProvider>
  );
}
